import * as React from 'react';
import { styled, css } from '../theme-provider';
import { Text, TextTag, TextContext, TokenTextColor, TokenTextAppearance, } from '../text';
import { getCommonProps } from '../helpers/common-props';
import { createA11yOutlineStyles } from '../helpers/a11y';
const StyledInteractionIndicator = styled.span.withConfig({ displayName: "StyledInteractionIndicator", componentId: "sc-fh8jn1" }) `color:${({ theme, disabled }) => disabled ? theme.colors.tab.disabled : theme.colors.tab.default};position:relative;top:-${({ theme }) => parseInt(theme.size.border.heavy, 10) / 2}px;${({ theme, isContainer }) => isContainer &&
    css `
			border-radius: ${theme.size.static650};
			padding: ${theme.size.static150} ${theme.size.static250};
		`} ${({ theme, isContainer, disabled, isSelected }) => isContainer &&
    !disabled &&
    css `
			:hover {
				background-color: ${isSelected ? theme.colors.tab.hover : 'none'};
				color: ${isSelected
        ? theme.colors.content.inverted
        : theme.colors.tab.hover};
			}
		`} ${({ disabled }) => disabled && 'cursor: not-allowed;'} ${({ disabled, isContainer, theme, isSelected }) => {
    if (isContainer && isSelected) {
        return css `
				color: ${theme.colors.content.inverted};
				background: ${disabled
            ? theme.colors.tab.disabled
            : theme.colors.tab.default};
			`;
    }
    return undefined;
}}:has(svg){svg{${({ theme }) => theme.direction === 'rtl'
    ? css `
							margin-left: ${theme.size.static150};
						`
    : css `
							margin-right: ${theme.size.static150};
						`}}}`;
const StyledTab = styled.button.withConfig({ displayName: "StyledTab", componentId: "sc-1knivlq" }) `position:relative;white-space:nowrap;border:0;box-shadow:none;background:none;${({ tabBarVerticalPosition, isContainer, 'aria-disabled': ariaDisabled, theme, }) => {
    if (ariaDisabled) {
        return;
    }
    if (isContainer) {
        return css `
				padding: ${theme.size.static100} 0;
			`;
    }
    return css `
			padding: 0;
			:hover,
			:focus {
				${StyledInteractionIndicator} {
					color: ${theme.colors.tab.hover};
				}

				&::before {
					width: var(--tabs-indicator-width);
					content: '';
					position: absolute;
					height: ${theme.size.line.default};
					background: ${theme.colors.tab.hover};

					${tabBarVerticalPosition}: 0;
				}
			}
		`;
}}:focus-visible{outline:none;${StyledInteractionIndicator}{${({ theme }) => createA11yOutlineStyles(theme, 'solid')};${({ theme, isContainer }) => !isContainer &&
    css `
					border-radius: ${theme.size.line.default};
					color: ${theme.colors.tab.hover};
					outline-offset: ${parseInt(theme.size.border.heavy, 10) / 2}px;
				`}}}${({ theme, 'aria-disabled': ariaDisabled, isContainer }) => ariaDisabled &&
    css `
			cursor: not-allowed;

			:focus-visible {
				${StyledInteractionIndicator} {
					${createA11yOutlineStyles(theme, 'solid')};
					${!isContainer &&
        css `
						border-radius: ${theme.size.line.default};
						color: ${theme.colors.tab.disabled};
						outline-offset: ${parseInt(theme.size.border.heavy, 10) / 2}px;
					`}
				}
			}
		`}`;
const StyledTabAnchor = styled.span.withConfig({ displayName: "StyledTabAnchor", componentId: "sc-j9jjx8" }) `display:inline-block;margin:0;border:none;background:transparent;padding:${({ theme, tabBarVerticalPosition }) => tabBarVerticalPosition === 'bottom' || tabBarVerticalPosition === 'top'
    ? `${theme.size.static150} 0`
    : 'inherit'};color:inherit;text-decoration:none;cursor:pointer;${({ isContainer }) => !isContainer &&
    css `
			:hover:enabled ${StyledInteractionIndicator} {
				color: ${({ theme }) => theme.colors.tab.hover};
				cursor: pointer;
			}
		`} ${({ disabled }) => disabled &&
    css `
			cursor: not-allowed;
		`}`;
export const Tab = React.forwardRef((props, forwardRef) => {
    const { isContainer, disabled, tabBarVerticalPosition, forwardKey, idPrefix, index, onClick, onMouseEnter, onMouseLeave, isSelected, title, } = props;
    const commonProps = getCommonProps(props);
    const handleClick = React.useCallback(() => {
        if (onClick && !disabled) {
            onClick(index);
        }
    }, [onClick, index, disabled]);
    const handleMouseEnter = React.useCallback((event) => {
        if (onMouseEnter) {
            onMouseEnter(event, isSelected);
        }
    }, [isSelected, onMouseEnter]);
    const handleMouseLeave = React.useCallback((event) => {
        if (onMouseLeave) {
            onMouseLeave(event, isSelected);
        }
    }, [onMouseLeave, isSelected]);
    const textContextValue = React.useMemo(() => ({
        bold: isSelected,
    }), [isSelected]);
    let titleTextElement = null;
    if (title) {
        if (typeof title === 'string') {
            titleTextElement = (React.createElement(Text, { tag: TextTag.span, color: TokenTextColor.inherit, appearance: TokenTextAppearance.label200 }, title));
        }
        else {
            titleTextElement = React.cloneElement(title, {
                color: TokenTextColor.inherit,
                tag: TextTag.span,
                appearance: TokenTextAppearance.label200,
            });
        }
    }
    return (React.createElement(StyledTab, { isContainer: isContainer, tabBarVerticalPosition: tabBarVerticalPosition, role: "tab", key: forwardKey, id: `${idPrefix}-tab-${index}`, "aria-controls": disabled ? undefined : `${idPrefix}-content-${index}`, "aria-selected": isSelected, "aria-disabled": disabled, 
        // disabled tabs should be programmatically focusabled for a11y standards (hence the -1)
        tabIndex: isSelected ? 0 : -1, index: index, onMouseEnter: disabled ? undefined : handleMouseEnter, onMouseLeave: disabled ? undefined : handleMouseLeave, isSelected: isSelected, onClick: handleClick, ref: forwardRef },
        React.createElement(StyledTabAnchor, Object.assign({ isContainer: isContainer, disabled: disabled, tabBarVerticalPosition: tabBarVerticalPosition }, commonProps),
            React.createElement(StyledInteractionIndicator, { isContainer: isContainer, disabled: disabled, isSelected: isSelected },
                React.createElement(TextContext.Provider, { value: textContextValue }, titleTextElement)))));
});
Tab.displayName = 'Tab';
